import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Resolver, useController, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export enum FIELDS {
  RECIPIENTS = 'recipients',
  SEND_TO_ME = 'sendCopy',
  OTHER_RECIPIENTS = 'otherRecipients',
  SUBJECT = 'subject',
  MESSAGE = 'message',
  ATTACHMENTS = 'attachments',
}

/** Total size limit for all email attachments (15 MiB). */
export const MAX_EMAIL_ATTACHMENTS_BYTES = 15 * 1024 * 1024;

export const MAX_OTHER_RECIPIENTS = 100;

export const parseOtherRecipients = (
  value: string | undefined | null,
): string[] => {
  if (!value?.trim()) return [];
  return value.split(/[\s,]+/).filter(Boolean);
};

export type EmailModuleFormValues = {
  [FIELDS.RECIPIENTS]: string[];
  [FIELDS.SEND_TO_ME]: boolean;
  [FIELDS.OTHER_RECIPIENTS]: string;
  [FIELDS.SUBJECT]: string;
  [FIELDS.MESSAGE]: string;
  [FIELDS.ATTACHMENTS]: any[];
};

export const useEmailModuleForm = (defaultValues: EmailModuleFormValues) => {
  const { t } = useTranslation();

  const resolver: Resolver<EmailModuleFormValues> = yupResolver(
    yup.object().shape({
      [FIELDS.SEND_TO_ME]: yup.bool(),
      [FIELDS.RECIPIENTS]: yup
        .array()
        .of(yup.string())
        .required(t('errors.required')),
      [FIELDS.OTHER_RECIPIENTS]: yup
        .array()
        .transform(function (value, originalValue) {
          if (this.isType(value) && value !== null) {
            return value;
          }
          return originalValue ? originalValue.split(/[\s,]+/) : [];
        })
        .of(
          yup
            .string()
            .email(({ value }) => `${t('errors.email')} (${value})`)
            .matches(
              /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
              {
                excludeEmptyString: true,
                message: ({ value }) => `${t('errors.email')} (${value})`,
              },
            ),
        ),
      [FIELDS.SUBJECT]: yup.string().required(t('errors.required')),
      [FIELDS.MESSAGE]: yup.string().required(t('errors.required')),
      [FIELDS.ATTACHMENTS]: yup
        .array()
        .test(
          'max-total-size',
          t('email.maxAttachmentsSize'),
          (files: unknown) => {
            if (!Array.isArray(files) || files.length === 0) return true;
            const total = files.reduce(
              (sum: number, f: { size?: number }) => sum + (f?.size ?? 0),
              0,
            );
            return total <= MAX_EMAIL_ATTACHMENTS_BYTES;
          },
        ),
    }),
  );

  const form = useForm<EmailModuleFormValues>({
    resolver,
    defaultValues,
  });

  const { field: attachmentsFieldProps } = useController({
    name: FIELDS.ATTACHMENTS,
    control: form.control,
    defaultValue: defaultValues[FIELDS.ATTACHMENTS],
  });

  const { field: recipientsFieldProps } = useController({
    name: FIELDS.RECIPIENTS,
    control: form.control,
    defaultValue: defaultValues[FIELDS.RECIPIENTS],
  });

  const { field: sendToMeFieldProps } = useController({
    name: FIELDS.SEND_TO_ME,
    control: form.control,
    defaultValue: defaultValues[FIELDS.SEND_TO_ME],
  });

  const values = useWatch({
    name: [
      FIELDS.SEND_TO_ME,
      FIELDS.RECIPIENTS,
      FIELDS.OTHER_RECIPIENTS,
      FIELDS.SUBJECT,
      FIELDS.MESSAGE,
      FIELDS.ATTACHMENTS,
    ],
    control: form.control,
    defaultValue: defaultValues,
  });

  return {
    ...form,
    values,
    attachmentsFieldProps,
    recipientsFieldProps,
    sendToMeFieldProps,
  };
};
